import { GridColDef, GridEditInputCell } from '@mui/x-data-grid-pro';
import { format } from 'date-fns';

import { getStartAndEndDateFields } from 'portal/src/components/common/forms/commonFormFields';
import {
  datePickerType,
  numberType,
  textInputType,
} from 'portal/src/components/common/forms/FormFieldTypes';
import addDateColumnProps from 'portal/src/components/common/tables/addDateColumnProps';
import { getStartAndEndDateInRowValidation } from 'portal/src/components/common/tables/inRowValidators';
import { numberFormatter } from 'portal/src/utils/numberFormatter';

export interface IHistoricalValueFormData<T> {
  startDate: Date;
  endDate: Date;
  value: T;
}

const parseIntegerValue = (value: unknown) => {
  const parsed = Number(value);

  if (Number.isNaN(parsed)) {
    return value;
  }

  return Math.trunc(parsed);
};

export const getColumns: (
  targetColumnType: 'number' | 'string',
  targetHeaderName: string,
  allowDecimal: boolean,
  maxDate: Date,
) => GridColDef[] = (
  targetColumnType,
  targetHeaderName,
  allowDecimal,
  maxDate,
) => {
  const maxDateError = `Date must be the same or before the latest historical value's start date (${format(maxDate, 'P')}).`;
  const startAndEndDateValidation = getStartAndEndDateInRowValidation({
    maxDate,
    maxDateError,
  });

  return [
    {
      field: 'startDate',
      editable: true,
      flex: 1,
      minWidth: 150,
      ...startAndEndDateValidation.startDate,
      ...addDateColumnProps('From Date'),
    },
    {
      field: 'endDate',
      editable: true,
      flex: 1,
      minWidth: 150,
      ...startAndEndDateValidation.endDate,
      ...addDateColumnProps('To Date'),
    },
    {
      field: 'value',
      type: targetColumnType,
      editable: true,
      headerName: targetHeaderName,
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 150,
      ...(targetColumnType === 'number' && {
        valueFormatter: (value) =>
          allowDecimal ? value : numberFormatter(value, { useGrouping: true }),
        valueParser: (value) =>
          allowDecimal ? value : parseIntegerValue(value),
        renderEditCell: (params) => (
          <GridEditInputCell
            {...params}
            {...(!allowDecimal && {
              value: parseIntegerValue(params.value),
              inputProps: { step: 1, inputMode: 'numeric' },
            })}
          />
        ),
      }),
    },
  ];
};

export const getFields = (
  maxDate: Date,
  targetHeaderName: string,
  targetColumnType: 'number' | 'string',
  allowDecimal: boolean,
): (datePickerType | textInputType | numberType)[] => [
  ...getStartAndEndDateFields({
    startLabel: 'From Date',
    endLabel: 'To Date',
    required: true,
    maxDate,
  }),
  {
    placeholder: targetHeaderName,
    label: targetHeaderName,
    name: 'value',
    required: true,
    ...(targetColumnType === 'string'
      ? { type: 'textInput' }
      : {
          type: 'number',
          allowNegative: false,
          autoDecimalFormat: allowDecimal,
        }),
  },
];
